import * as React from "react";
import styled from "styled-components";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import Grid from "@mui/material/Grid";
import request from "src/utils/request";
import { Box, Button, ListItemButton, Paper, Stack, Typography } from "@mui/material";
import AvatarBadge from "src/components/avatar-badge";
import Checkbox from "@mui/material/Checkbox";
import ListItemIcon from "@mui/material/ListItemIcon";
import { ToastContext } from "src/components/providers/toast-provider";
import InvitationModal from "src/scenes/subscription/invitation-modal";
import LoadingButton from "@mui/lab/LoadingButton";

const SubscriptionPaper = styled(Paper)`
    border-radius: 14px;
`;
const MainText = styled.p`
    font-size: 12px;
    margin: 0;
`;
const SecondaryText = styled.p`
    font-size: 11px;
    font-weight: 300;
    opacity: 0.7;
    margin: 0;
`;
const CardTitle = styled.span`
    font-size: 18px;
    font-weight: 500;
    padding: 4px 0px 4px;
`;
const RemoveButton = styled(Button)`
    font-size: 12px;
    font-weight: 400;
    text-transform: none;
    color: ${({ theme }) => theme.palette.warning.main};
`;

const HelperText = styled(Typography)`
    font-size: 12px;
    color: ${({ theme, isValidation }) =>
        isValidation ? theme.palette.primary.main : theme.palette.secondary.main};
`;
const TeamPlanListUpdated = ({ subscription, planMembersApi, setMembersAlreadyInPlan }) => {
    if (!subscription) return null;
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const [data, setData] = React.useState(" ");
    const [checked, setChecked] = React.useState([]);
    const [openInvitationModal, setOpenInvitationModal] = React.useState(false);
    const [loading] = React.useState(false);
    const handleInvitationOpen = () => setOpenInvitationModal(!openInvitationModal);

    React.useEffect(() => {
        setData(planMembersApi.data && planMembersApi.data.members);
        setMembersAlreadyInPlan(planMembersApi.data && planMembersApi.data.members);
    }, [planMembersApi.status]);

    const handleToggle = (value) => () => {
        const currentIndex = checked.indexOf(value);
        const newChecked = [...checked];

        if (currentIndex === -1) {
            newChecked.push(value);
        } else {
            newChecked.splice(currentIndex, 1);
        }
        setChecked(newChecked);
    };

    const handleRemoveFromPlanRequest = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        try {
            const memberIds = data.map((member) => member.id);
            const memberIdsToRemove = checked.map((member) => member.id);
            const survivingPlanMembers = memberIds.filter(
                (member) => !memberIdsToRemove.includes(member)
            );
            await request(`/api/payments/${subscription.id}/members`, {
                method: "PUT",
                body: JSON.stringify({ memberIds: survivingPlanMembers }),
            });
            setMessage(`Successfully removed members from ${subscription.name} Plan`);
            setSeverity("success");
            setIsToastTriggered(true);
            setChecked([]);
        } catch (error) {
            setMessage(`Failed to remove members from ${subscription.name} Plan.`);
            setSeverity("error");
            setIsToastTriggered(true);
            setChecked([]);
        }
    };

    if (planMembersApi && planMembersApi.status !== "SUCCESS") return null;
    return (
        <Grid container spacing={2}>
            <Box pb={3} pt={3} sx={{ width: "100%" }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography>Manage {subscription.name} Plan</Typography>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={2}
                    >
                        <Stack justifyContent="space-between">
                            <HelperText>
                                Is somebody missing someone from your organisation list?
                            </HelperText>
                        </Stack>
                        <Stack direction="row" spacing={2}>
                            <LoadingButton
                                color="success"
                                variant="contained"
                                loading={loading}
                                onClick={handleInvitationOpen}
                            >
                                Invite
                            </LoadingButton>
                        </Stack>
                    </Stack>
                </Stack>
            </Box>

            <Grid item xs={12} md={12}>
                <SubscriptionPaper>
                    <Stack direction="column">
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            style={{ padding: "17px 26px 9px" }}
                        >
                            <CardTitle>Manage team plan members</CardTitle>
                            {checked.length > 0 && (
                                <RemoveButton onClick={handleRemoveFromPlanRequest}>
                                    Remove from plan
                                </RemoveButton>
                            )}
                        </Stack>
                        <List
                            dense
                            sx={{
                                width: "100%",
                                maxHeight: "22rem",
                                overflow: "auto",
                                paddingBottom: "28px",
                            }}
                        >
                            {data &&
                                data.map((member) => {
                                    return (
                                        <ListItem key={member}>
                                            <ListItemButton
                                                role={undefined}
                                                onClick={handleToggle(member)}
                                                dense
                                            >
                                                <ListItemIcon style={{ minWidth: "48px" }}>
                                                    <Checkbox
                                                        checked={checked.indexOf(member) !== -1}
                                                        tabIndex={-1}
                                                        disableRipple
                                                        size="small"
                                                        inputProps={{
                                                            "aria-labelledby": "label",
                                                        }}
                                                    />
                                                </ListItemIcon>
                                                <ListItemAvatar>
                                                    <AvatarBadge
                                                        style={{ marginRight: "16px" }}
                                                        name={member.displayName}
                                                        picture={member.picture}
                                                    />
                                                </ListItemAvatar>
                                                <Stack direction="column">
                                                    <MainText>{member.displayName}</MainText>
                                                    <SecondaryText>{member.email}</SecondaryText>
                                                </Stack>
                                            </ListItemButton>
                                        </ListItem>
                                    );
                                })}
                        </List>
                    </Stack>
                </SubscriptionPaper>
                <InvitationModal
                    open={openInvitationModal}
                    handleClose={setOpenInvitationModal}
                    subscription={subscription}
                />
            </Grid>
        </Grid>
    );
};

export default TeamPlanListUpdated;
